import {
	confluenceLocalStorageInstance as localStorage,
	PERSISTED_KEYS_ON_SERVER,
} from '@confluence/storage-manager';
import { getLogger } from '@confluence/logger';

const logger = getLogger('EditorControlsBannerDismissal');

export type BannerType = 'toolbar-top' | 'toolbar-inline';
type BannerDismissal = {
	time?: number;
	count?: number;
};

type LocalStorageItem = {
	[key in BannerType]?: BannerDismissal;
};

const MAX_DISMISS_COUNT = 2;
const ONE_DAY_IN_MS = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
const ONE_WEEK_IN_MS = 7 * ONE_DAY_IN_MS; // 1 week in milliseconds
const STORAGE_KEY = PERSISTED_KEYS_ON_SERVER.PERSISTED_EDITOR_CONTROLS_ONBOARDING_BANNER_DISMISSED;
const DISMISSAL_PERIODS = [
	ONE_DAY_IN_MS, // 1st dismissal: 24 hours
	ONE_WEEK_IN_MS, // 2nd dismissal: 1 week
];

export const getBannerType = (isTopBanner: boolean): BannerType => {
	return isTopBanner ? 'toolbar-top' : 'toolbar-inline';
};

const getLocalStorageItem = (): LocalStorageItem | undefined => {
	try {
		const itemString = localStorage.getItem(STORAGE_KEY);
		if (!itemString) {
			return undefined;
		}
		const item = JSON.parse(itemString);
		if (
			typeof item === 'object' &&
			item !== null &&
			!Array.isArray(item) &&
			Object.keys(item).every((key) => ['toolbar-top', 'toolbar-inline'].includes(key))
		) {
			return item;
		}
	} catch (error) {
		logger.error`Error parsing local storage key(${STORAGE_KEY}): ${error}`;
	}
	return undefined;
};

export const isBannerDismissedInLocalStorage = (bannerType: BannerType): boolean => {
	const localStorageItem = getLocalStorageItem();
	if (!localStorageItem || !localStorageItem[bannerType]) {
		return false;
	}
	const dismissal = localStorageItem[bannerType];
	const { time = 0, count = 0 } = dismissal ?? {};
	const currentTime = Date.now();
	if (count >= MAX_DISMISS_COUNT) {
		return true; // Permanently dismissed
	}
	const requiredDuration = DISMISSAL_PERIODS[count] ?? 0;
	return currentTime - time < requiredDuration;
};

export const persistBannerDismissal = (bannerType: BannerType) => {
	let item = getLocalStorageItem();
	if (!item) {
		item = { [bannerType]: { time: Date.now(), count: 0 } };
	} else {
		const currentTime = Date.now();
		if (!item[bannerType]) {
			item[bannerType] = { time: currentTime, count: 0 };
		} else {
			const newCount = (item[bannerType]?.count ?? 0) + 1;
			item[bannerType]!.time = currentTime;
			item[bannerType]!.count = Math.min(newCount, MAX_DISMISS_COUNT);
		}
	}

	try {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(item));
	} catch (error) {
		logger.error`Error setting local storage key(${STORAGE_KEY}): ${error}`;
	}
};
